import { forwardRef, type ReactNode, type HTMLAttributes } from 'react';
import { cn } from '@vitness/theme';

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Optional leading icon, rendered in a muted circular surface above the title. */
  icon?: ReactNode;
  /** Primary heading describing the empty/zero-result state. */
  title: ReactNode;
  /** Optional supporting copy explaining the state or a next step. */
  description?: ReactNode;
  /** Optional action slot (e.g. a Button) rendered below the description. */
  action?: ReactNode;
}

/**
 * Centered zero-state for empty lists, no search results, or first-run views.
 * Composed of an optional icon, a title, optional description, and an optional
 * action slot. Token-driven surfaces and text colors keep it theme-aware.
 */
export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { icon, title, description, action, className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col items-center justify-center text-center px-vn-6 py-vn-8',
        className,
      )}
      {...props}
    >
      {icon && (
        <div className="flex items-center justify-center mb-vn-4 size-12 rounded-vn-full bg-surface-card-hover text-text-muted">
          {icon}
        </div>
      )}
      <p className="font-display text-text-primary font-semibold text-base">{title}</p>
      {description && (
        <p className="mt-vn-2 max-w-sm font-body text-text-secondary text-sm">{description}</p>
      )}
      {action && <div className="mt-vn-5">{action}</div>}
    </div>
  );
});
